import type { TFunction } from 'i18next';

import {
  CreateUpdateBlockadeMonthExceptionDto,
  CreateUpdateBlockadeMonthExceptionDtoFromJSON,
  DefaultApi,
  GetBlockadeMonthExceptionDto,
  GetLastEditorDto,
} from '../autogenerated/pokApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { BlockadeMonthExceptionSchema } from '../validation/schemas';
import { TKeys } from '../../hooks/useTranslation';
import { blockadeMonthExceptionForGrid } from '../../../pok/components/BlockadeMonthException/BlockadeMonthExceptionSearch';

export interface BlockadeMonthContextExceptionInterface {
  getById: (id: string) => Promise<GetBlockadeMonthExceptionDto>;
  getActiveByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getActiveCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  update: (
    id: string,
    dto: CreateUpdateBlockadeMonthExceptionDto,
  ) => Promise<void>;
  create: (
    dto: CreateUpdateBlockadeMonthExceptionDto,
  ) => Promise<GetBlockadeMonthExceptionDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  deactivate: (id: string) => Promise<void>;
}

export const BlockadeMonthExceptionContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.blockadeMonthExceptionControllerGet(id),
    getActiveByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.blockadeMonthExceptionControllerGetActiveByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => blockadeMonthExceptionForGrid(t, tk, item));
    },
    getActiveCount: async (filterText?: string, filterColumns?: string) =>
      api.blockadeMonthExceptionControllerGetActiveCount(
        filterText || '',
        filterColumns,
      ),
    getAllByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.blockadeMonthExceptionControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => blockadeMonthExceptionForGrid(t, tk, item));
    },
    getAllCount: async (filterText?: string, filterColumns?: string) =>
      api.blockadeMonthExceptionControllerGetAllCount(
        filterText || '',
        filterColumns,
      ),
    update: (id: string, dto: CreateUpdateBlockadeMonthExceptionDto) =>
      api.blockadeMonthExceptionControllerUpdate(id, dto),
    create: (dto: CreateUpdateBlockadeMonthExceptionDto) =>
      api.blockadeMonthExceptionControllerCreate(dto),
    getLastEditor: (id: string) =>
      api.blockadeMonthExceptionControllerGetLastEditor(id),
    deactivate: (id: string) =>
      api.blockadeMonthExceptionControllerDeactivate(id),
  };
};

export const newBlockadeExceptionMonth = (projectId?: string) => {
  const dateToDelete = new Date();
  dateToDelete.setDate(dateToDelete.getDate() + 7);

  return CreateUpdateBlockadeMonthExceptionDtoFromJSON({
    projectId,
    isSalesUnblocked: false,
    isPurchaseUnblocked: false,
    active: true,
    dateToDelete: dateToDelete,
  });
};

export const validate = (
  blockadeException: CreateUpdateBlockadeMonthExceptionDto,
) => validateAgainst(BlockadeMonthExceptionSchema, blockadeException);

export const convert = (
  blockade?: GetBlockadeMonthExceptionDto,
  projectId?: string,
) => {
  if (!blockade) {
    return newBlockadeExceptionMonth(projectId);
  }

  const converted = CreateUpdateBlockadeMonthExceptionDtoFromJSON(blockade);
  converted.projectId = blockade.project?.id;

  return converted;
};
